import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/lib/api'
import { idempotencyKey } from '@/lib/utils'
import type { components } from '@/api/schema'

/**
 * Help area: help centre, support requests, what's new, system status.
 * Keys start with ['help', …].
 */

type S = components['schemas']
export type HelpArticleList = S['HelpArticleList']
export type HelpArticleSummary = S['HelpArticleSummary']
export type HelpArticle = S['HelpArticle']
export type SupportTicket = S['SupportTicket']
export type SupportTicketDetail = S['SupportTicketDetail']
export type SupportTicketMessage = S['SupportTicketMessage']
export type SupportTicketAttachment = S['SupportTicketAttachment']
export type WhatsNew = S['WhatsNew']
export type ChangelogEntry = S['ChangelogEntry']
export type SystemStatus = S['SystemStatus']
export type StatusIncident = S['StatusIncident']
export type MaintenanceWindow = S['MaintenanceWindow']

/* ─────────────────────────── help centre ─────────────────────────── */

export const articlesQuery = (p: { q?: string; section?: string; screen?: string } = {}) =>
  queryOptions({
    queryKey: ['help', 'articles', p.q ?? '', p.section ?? '', p.screen ?? ''] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/help/articles', { params: { query: { ...(p.q ? { q: p.q } : {}), ...(p.section ? { section: p.section } : {}), ...(p.screen ? { screen: p.screen } : {}) } } })),
    staleTime: 60_000,
  })

export const articleQuery = (slug: string) =>
  queryOptions({ queryKey: ['help', 'article', slug] as const, queryFn: () => unwrap(api.GET('/esm/v1/help/articles/{slug}', { params: { path: { slug } } })) })

export function useArticleFeedback(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { helpful: boolean; comment?: string }) => unwrap(api.POST('/esm/v1/help/articles/{slug}/feedback', { params: { path: { slug } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['help', 'article', slug] }),
  })
}

/* ─────────────────────────── support requests ─────────────────────────── */

export const ticketsQuery = (p: { status: 'active' | 'resolved' | 'all'; scope: 'mine' | 'all' }) =>
  queryOptions({ queryKey: ['help', 'tickets', p.status, p.scope] as const, queryFn: () => unwrap(api.GET('/esm/v1/help/tickets', { params: { query: { status: p.status, scope: p.scope, limit: 100 } } })) })

export const ticketQuery = (id: string) =>
  queryOptions({ queryKey: ['help', 'ticket', id] as const, queryFn: () => unwrap(api.GET('/esm/v1/help/tickets/{ticket_id}', { params: { path: { ticket_id: id } } })) })

export type NewTicket = {
  category: SupportTicket['category']
  severity: SupportTicket['severity']
  subject: string
  message: string
  context?: { screen?: string; route?: string; workspace_id?: string }
}

export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, ...body }: NewTicket & { key: string }) => unwrap(api.POST('/esm/v1/help/tickets', { body, headers: { 'Idempotency-Key': key } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['help', 'tickets'] }),
  })
}

export function useReplyTicket(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { body: string }) => unwrap(api.POST('/esm/v1/help/tickets/{ticket_id}/messages', { params: { path: { ticket_id: id } }, body })),
    onSuccess: d => {
      qc.setQueryData(['help', 'ticket', id], d)
      void qc.invalidateQueries({ queryKey: ['help', 'tickets'] })
    },
  })
}

export function useTicketStatus(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (status: 'resolved' | 'open') => unwrap(api.POST('/esm/v1/help/tickets/{ticket_id}/status', { params: { path: { ticket_id: id } }, body: { status } })),
    onSuccess: d => {
      qc.setQueryData(['help', 'ticket', id], d)
      void qc.invalidateQueries({ queryKey: ['help', 'tickets'] })
    },
  })
}

/** Read a File as base64 (no data: prefix). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''))
    r.onerror = () => reject(new Error('That file could not be read.'))
    r.readAsDataURL(file)
  })
}

export const ATTACHMENT_MAX = 5 * 1024 * 1024

export function useAttachFile(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) =>
      unwrap(api.POST('/esm/v1/help/tickets/{ticket_id}/attachments', { params: { path: { ticket_id: id } }, body: { name: file.name, content_type: file.type || 'application/octet-stream', data_base64: await fileToBase64(file) } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['help', 'ticket', id] }),
  })
}

export const attachmentUrl = (ticketId: string, attachmentId: string) => `/esm/v1/help/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`

/* ─────────────────────────── what's new ─────────────────────────── */

export const whatsNewQuery = (category?: ChangelogEntry['category']) =>
  queryOptions({ queryKey: ['help', 'whats-new', category ?? 'all'] as const, queryFn: () => unwrap(api.GET('/esm/v1/help/whats-new', { params: { query: { ...(category ? { category } : {}), limit: 100 } } })) })

/** For the shell's unread dot (foundation can use this once wired). */
export const whatsNewUnreadQuery = () =>
  queryOptions({ queryKey: ['help', 'whats-new-unread'] as const, queryFn: () => unwrap(api.GET('/esm/v1/help/whats-new/unread')), staleTime: 5 * 60_000 })

export function useMarkWhatsNewSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => unwrap(api.POST('/esm/v1/help/whats-new/seen')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['help', 'whats-new'] })
      void qc.invalidateQueries({ queryKey: ['help', 'whats-new-unread'] })
    },
  })
}

/* ─────────────────────────── status ─────────────────────────── */

export const statusQuery = () =>
  queryOptions({ queryKey: ['help', 'status'] as const, queryFn: () => unwrap(api.GET('/esm/v1/status')), refetchInterval: 60_000 })

export const newIdempotencyKey = idempotencyKey
